import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Typography,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { API_ORIGIN } from '../../api';

function money(v, currency) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'GBP' }).format(n);
  } catch {
    return `${n.toFixed(2)}`;
  }
}

/**
 * Shared estimate detail + accept/reject UI (public link or client portal).
 */
export default function EstimateDetailView({
  fetchEstimate,
  postAction,
  pdfHref,
  compact = false,
}) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  const mayRespond = useMemo(() => {
    const st = data?.status;
    return st !== 'accepted' && st !== 'rejected';
  }, [data?.status]);

  const reload = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const json = await fetchEstimate();
      setData(json);
    } catch (e) {
      setError(e.message || 'Failed to load estimate');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [fetchEstimate]);

  useEffect(() => {
    reload();
  }, [reload]);

  async function handleAction(action) {
    if (!postAction) return;
    setError('');
    setBusy(true);
    try {
      await postAction(action);
      await reload();
    } catch (e) {
      setError(e.message || 'Action failed');
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={compact ? 28 : 36} />
      </Box>
    );
  }

  if (!data) {
    return error ? <Alert severity="error">{error}</Alert> : null;
  }

  const pdfUrl = pdfHref || (data.public_token ? `${API_ORIGIN}/api/public/estimates/${data.public_token}/pdf` : null);

  return (
    <Box>
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      ) : null}

      <Card variant="outlined" sx={{ borderRadius: compact ? 2 : 3 }}>
        <CardContent sx={{ p: compact ? 2 : 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 900 }}>
                {data.client_company ? `${data.client_company} · ` : ''}
                {data.client_name || 'Estimate'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Status: <b>{data.status}</b>
                {data.valid_until ? ` · Valid until ${data.valid_until}` : ''}
              </Typography>
            </Box>
            <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
              <Typography sx={{ fontWeight: 900 }}>Total: {money(data.total, data.currency)}</Typography>
              {pdfUrl ? (
                <Button
                  component="a"
                  href={pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="small"
                  variant="outlined"
                  startIcon={<PictureAsPdfIcon />}
                  sx={{ textTransform: 'none', mt: 1 }}
                >
                  Download PDF
                </Button>
              ) : null}
            </Box>
          </Box>

          {data.collection_id ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              Job in our system: <b>#{data.collection_id}</b>
            </Alert>
          ) : null}

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {(data.lines || []).map((l) => (
              <Box key={l.id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 800 }}>{l.title}</Typography>
                  {l.description ? (
                    <Typography variant="body2" color="text.secondary">
                      {l.description}
                    </Typography>
                  ) : null}
                  <Typography variant="caption" color="text.secondary">
                    {Number(l.quantity)} × {money(l.unit_price, data.currency)}
                  </Typography>
                </Box>
                <Typography sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}>
                  {money(l.line_total, data.currency)}
                </Typography>
              </Box>
            ))}
          </Box>

          {data.notes ? (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography sx={{ fontWeight: 800, mb: 0.5 }}>Notes</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                {data.notes}
              </Typography>
            </>
          ) : null}

          {postAction ? (
            <>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                {mayRespond ? (
                  <>
                    <Button
                      variant="outlined"
                      disabled={busy}
                      onClick={() => handleAction('reject')}
                      sx={{ textTransform: 'none' }}
                    >
                      Reject
                    </Button>
                    <Button
                      variant="contained"
                      disabled={busy}
                      onClick={() => handleAction('accept')}
                      sx={{ textTransform: 'none' }}
                    >
                      Accept estimate
                    </Button>
                  </>
                ) : (
                  <Alert severity={data.status === 'accepted' ? 'success' : 'info'} sx={{ width: '100%' }}>
                    {data.status === 'accepted'
                      ? 'You have accepted this estimate. We will be in touch to confirm.'
                      : 'This estimate has been declined.'}
                  </Alert>
                )}
              </Box>
            </>
          ) : null}
        </CardContent>
      </Card>
    </Box>
  );
}
